import React, { useState, useEffect } from "react";
import { supabase } from "../../lib/supabase";
import { fetchAffiliatePlatforms } from "../../services/affiliatePlatformsService";
import { CardManagementSection } from "../billing/CardManagementSection";
import { PaymentHistorySection } from "../billing/PaymentHistorySection";
import { BILLING_METHOD_LABELS } from "../../constants/billing";

const selectStyle = (canEdit, extraMarginBottom) => ({
  width:"100%", boxSizing:"border-box", padding:"12px 32px 12px 14px",
  background:`#FAFAFA url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8' fill='none' stroke='%23626262' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'><path d='M1 1.5L6 6.5L11 1.5'/></svg>") no-repeat right 14px center / 10px 6px`,
  border:"1px solid #E2E2E2", borderRadius:10, color:"#151515", fontSize:14, fontFamily:"inherit", outline:"none",
  appearance:"none", WebkitAppearance:"none", MozAppearance:"none",
  cursor: canEdit ? "pointer" : "not-allowed", opacity: canEdit ? 1 : 0.65,
  marginBottom: extraMarginBottom ? 10 : 0,
});

const EMPTY_METHOD = { billingMethod: "", affiliatePlatform: "", affiliatePlatformCustomName: "" };

function BillingMethodValue({ value }) {
  const label = value.billingMethod ? BILLING_METHOD_LABELS[value.billingMethod] || value.billingMethod : "Not set";
  return (
    <div style={{ fontSize:13, color:"#3E3E3E" }}>
      {label}
      {value.billingMethod === "affiliate_platform" && value.affiliatePlatform && (
        <> — {value.affiliatePlatform === "Custom" ? value.affiliatePlatformCustomName || "Custom" : value.affiliatePlatform}</>
      )}
    </div>
  );
}

// BillingMethodSection — the Billing tab's "Billing Method" dropdown
// (credit card, invoice, or an affiliate platform, DEV-1960). Writes
// advertisers.billing_method/affiliate_platform/affiliate_platform_custom_name
// directly through the Supabase client, scoped by effectiveAdvertiserId.
// Editable only by internal support (@audiohook.com) — everyone else sees a
// read-only value and a note to contact support.
//
// When the saved method is 'credit_card', renders CardManagementSection
// directly inside this same card, below the saved value — cards on file are
// a detail of that billing method, not a separate concern, so there's no
// standalone "Cards on File" card anymore. CardManagementSection has no
// outer chrome of its own for exactly this reason (see its own comment).
//
// The initial `advertisers` row is fetched once by UserMenu (shared with
// BillingDetailsSection) and passed in as `initialData`/`loading` so the
// Billing tab doesn't issue two separate reads of the same row. This
// component still fetches the affiliate-platform catalog on its own since
// that's a separate data source.
export function BillingMethodSection({ advertiserId, isSupportUser, authToken, canManageBilling, initialData, loading, onSaved, onClose }) {
  const [saved, setSaved] = useState(EMPTY_METHOD);
  const [draft, setDraft] = useState(EMPTY_METHOD);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [platforms, setPlatforms] = useState([]);
  const [platformsError, setPlatformsError] = useState(null);

  useEffect(() => {
    if (loading) return;
    const next = {
      billingMethod: initialData?.billing_method ?? "",
      affiliatePlatform: initialData?.affiliate_platform ?? "",
      affiliatePlatformCustomName: initialData?.affiliate_platform_custom_name ?? "",
    };
    setSaved(next);
    setDraft(next);
  }, [initialData, loading]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await fetchAffiliatePlatforms();
        if (cancelled) return;
        setPlatforms(result);
        setPlatformsError(null);
      } catch (err) {
        if (cancelled) return;
        setPlatforms([]);
        setPlatformsError(err?.message || "Failed to load affiliate platform list");
        console.error("affiliate-platforms fetch failed:", err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const startEdit = () => {
    setDraft(saved);
    setError(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    setDraft(saved);
    setError(null);
    setEditing(false);
  };

  const handleSave = async () => {
    if (!supabase || !advertiserId || !isSupportUser) return;
    if (draft.billingMethod === "affiliate_platform" && draft.affiliatePlatform === "Custom" && !draft.affiliatePlatformCustomName.trim()) {
      setError("Enter a platform name for Custom.");
      return;
    }
    setSaving(true);
    setError(null);
    const nextAffiliatePlatform = draft.billingMethod === "affiliate_platform" ? (draft.affiliatePlatform || null) : null;
    const nextCustomName = draft.billingMethod === "affiliate_platform" && draft.affiliatePlatform === "Custom" ? (draft.affiliatePlatformCustomName || null) : null;
    let saveError = null;
    try {
      ({ error: saveError } = await supabase
        .from("advertisers")
        .update({
          billing_method: draft.billingMethod || null,
          affiliate_platform: nextAffiliatePlatform,
          affiliate_platform_custom_name: nextCustomName,
        })
        .eq("id", advertiserId));
    } catch (err) {
      saveError = err;
    } finally {
      setSaving(false);
    }
    if (saveError) {
      setError(saveError.message || "Failed to save billing method");
      return;
    }
    const nextSaved = { billingMethod: draft.billingMethod, affiliatePlatform: nextAffiliatePlatform ?? "", affiliatePlatformCustomName: nextCustomName ?? "" };
    setSaved(nextSaved);
    setDraft(nextSaved);
    setEditing(false);
    onSaved?.();
  };

  const isCreditCard = saved.billingMethod === "credit_card";

  return (
    <div style={{ background:"#FAFAFA", border:"1px solid #E2E2E2", borderRadius:12, padding:16, marginBottom:16 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
        <div style={{ fontSize:11, color:"#626262", textTransform:"uppercase", letterSpacing:1 }}>Billing Method</div>
        {!loading && isSupportUser && !editing && (
          <button
            type="button"
            onClick={startEdit}
            style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"6px 12px", background:"transparent", color:"#626262", border:"1px solid #E2E2E2", borderRadius:8, fontSize:11, fontWeight:600, cursor:"pointer" }}
          >
            {saved.billingMethod ? (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
              </svg>
            ) : (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            )}
            {saved.billingMethod ? "Change" : "Set up"}
          </button>
        )}
      </div>
      {loading ? (
        <div style={{ fontSize:12, color:"#7b7b7b" }}>Loading…</div>
      ) : !isSupportUser ? (
        <>
          <BillingMethodValue value={saved} />
          <div style={{ marginTop:6, fontSize:12, color:"#7b7b7b" }}>Contact support to change your billing method.</div>
          {isCreditCard && (
            <>
              <CardManagementSection
                advertiserId={advertiserId}
                authToken={authToken}
                billingMethod={saved.billingMethod}
                canManageBilling={canManageBilling}
                loading={loading}
              />
              <PaymentHistorySection advertiserId={advertiserId} authToken={authToken} loading={loading} canManageBilling={canManageBilling} onClose={onClose} isSupportUser={isSupportUser} />
            </>
          )}
        </>
      ) : !editing ? (
        <>
          <BillingMethodValue value={saved} />
          {isCreditCard && (
            <>
              <CardManagementSection
                advertiserId={advertiserId}
                authToken={authToken}
                billingMethod={saved.billingMethod}
                canManageBilling={canManageBilling}
                loading={loading}
              />
              <PaymentHistorySection advertiserId={advertiserId} authToken={authToken} loading={loading} canManageBilling={canManageBilling} onClose={onClose} isSupportUser={isSupportUser} />
            </>
          )}
        </>
      ) : (
        <>
          <select
            value={draft.billingMethod}
            onChange={(e) => setDraft((prev) => ({ ...prev, billingMethod: e.target.value }))}
            disabled={saving}
            style={selectStyle(true, draft.billingMethod === "affiliate_platform")}
          >
            <option value="">Select billing method…</option>
            <option value="credit_card">Credit Card</option>
            <option value="invoice">Invoice</option>
            <option value="affiliate_platform">Affiliate Platform</option>
          </select>

          {draft.billingMethod === "affiliate_platform" && (
            <select
              value={draft.affiliatePlatform}
              onChange={(e) => setDraft((prev) => ({ ...prev, affiliatePlatform: e.target.value }))}
              disabled={saving}
              style={selectStyle(true, draft.affiliatePlatform === "Custom")}
            >
              <option value="">Select platform…</option>
              {draft.affiliatePlatform && draft.affiliatePlatform !== "Custom" && !platforms.includes(draft.affiliatePlatform) && (
                <option value={draft.affiliatePlatform}>{draft.affiliatePlatform}</option>
              )}
              {platforms.map((platform) => (
                <option key={platform} value={platform}>{platform}</option>
              ))}
              <option value="Custom">Custom</option>
            </select>
          )}

          {draft.billingMethod === "affiliate_platform" && platformsError && (
            <div style={{ marginTop:-4, marginBottom:10, fontSize:11, color:"#e53935" }}>
              Couldn't load the platform list ({platformsError}). Only "Custom" is available.
            </div>
          )}

          {draft.billingMethod === "affiliate_platform" && draft.affiliatePlatform === "Custom" && (
            <input
              value={draft.affiliatePlatformCustomName}
              onChange={(e) => setDraft((prev) => ({ ...prev, affiliatePlatformCustomName: e.target.value }))}
              disabled={saving}
              placeholder="Platform name"
              style={{ width:"100%", boxSizing:"border-box", background:"#FAFAFA", border:"1px solid #E2E2E2", borderRadius:10, padding:"12px 14px", color:"#151515", fontSize:14, outline:"none", fontFamily:"inherit" }}
            />
          )}

          {error && (
            <div style={{ marginTop:8, fontSize:12, color:"#e53935" }}>{error}</div>
          )}

          <div style={{ display:"flex", gap:8, marginTop:10 }}>
            <button
              type="button"
              onClick={cancelEdit}
              disabled={saving}
              style={{ flex:1, padding:"10px 0", background:"transparent", color:"#626262", border:"1px solid #E2E2E2", borderRadius:10, fontSize:13, cursor: saving ? "not-allowed" : "pointer" }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              style={{ flex:1, padding:"10px 0", background:"#F90091", color:"#fff", border:"none", borderRadius:10, fontSize:13, fontWeight:600, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1 }}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
